/**
 * Camera Service
 * 
 * Servicio nativo para acceso a cámara y galería de fotos usando Capacitor.
 * Proporciona funcionalidades para tomar fotos o seleccionar de la galería.
 * 
 * @module native/camera
 */

import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('CameraService');

export interface CameraPhoto {
  /** Base64 o URL de la imagen */
  dataUrl: string;
  /** Formato de la imagen */
  format: string;
  /** Path del archivo (solo en nativo) */
  path?: string;
  /** Blob del archivo para subir */
  blob?: Blob;
}

export interface CameraOptions {
  /** Calidad de la imagen 0-100 */
  quality?: number;
  /** Ancho máximo en píxeles */
  width?: number;
  /** Altura máxima en píxeles */
  height?: number;
  /** Si debe corregir orientación automáticamente */
  correctOrientation?: boolean;
}

class CameraService {
  private isNative: boolean;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Verifica si el dispositivo tiene cámara disponible
   */
  async checkPermissions(): Promise<boolean> {
    try {
      if (!this.isNative) {
        // En web, verificar soporte de MediaDevices
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      }

      const permissions = await Camera.checkPermissions();
      return permissions.camera === 'granted' || permissions.camera === 'prompt';
    } catch (error) {
      logger.error('Error verificando permisos de cámara', { error });
      return false;
    }
  }

  /**
   * Solicita permisos de cámara
   */
  async requestPermissions(): Promise<boolean> {
    try {
      if (!this.isNative) {
        return true; // En web, el navegador lo maneja
      }

      const permissions = await Camera.requestPermissions();
      return permissions.camera === 'granted';
    } catch (error) {
      logger.error('Error solicitando permisos de cámara', { error });
      return false;
    }
  }

  /**
   * Toma una foto con la cámara
   */
  async takePhoto(options: CameraOptions = {}): Promise<CameraPhoto | null> {
    try {
      logger.info('Iniciando captura de foto con cámara');

      const photo = await Camera.getPhoto({
        quality: options.quality ?? 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: options.width ?? 800,
        height: options.height ?? 800,
        correctOrientation: options.correctOrientation ?? true,
        presentationStyle: 'fullscreen',
        promptLabelHeader: 'Foto de perfil',
        promptLabelPhoto: 'Elegir de galería',
        promptLabelPicture: 'Tomar foto'
      });

      return this.processPhoto(photo);
    } catch (error: any) {
      if (error.message?.includes('User cancelled')) {
        logger.info('Usuario canceló la captura de foto');
        return null;
      }
      logger.error('Error tomando foto', { error });
      throw new Error('No se pudo tomar la foto. Verifica los permisos de cámara.');
    }
  }

  /**
   * Selecciona una foto de la galería
   */
  async pickFromGallery(options: CameraOptions = {}): Promise<CameraPhoto | null> {
    try {
      logger.info('Abriendo galería de fotos');

      const photo = await Camera.getPhoto({
        quality: options.quality ?? 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        width: options.width ?? 800,
        height: options.height ?? 800,
        correctOrientation: options.correctOrientation ?? true
      });

      return this.processPhoto(photo);
    } catch (error: any) {
      if (error.message?.includes('User cancelled')) {
        logger.info('Usuario canceló la selección de foto');
        return null;
      }
      logger.error('Error seleccionando foto de galería', { error });
      throw new Error('No se pudo acceder a la galería. Verifica los permisos.');
    }
  }

  /**
   * Muestra un selector con opciones: cámara o galería
   * En iOS nativo muestra el action sheet nativo de Capacitor
   */
  async pickPhotoWithPrompt(options: CameraOptions = {}): Promise<CameraPhoto | null> {
    try {
      logger.info('Mostrando selector de foto (cámara o galería)');

      const photo = await Camera.getPhoto({
        quality: options.quality ?? 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt, // Muestra opciones: cámara o galería
        width: options.width ?? 800,
        height: options.height ?? 800,
        correctOrientation: options.correctOrientation ?? true,
        presentationStyle: 'popover',
        promptLabelHeader: 'Foto de perfil',
        promptLabelPhoto: 'Elegir de galería',
        promptLabelPicture: 'Tomar foto',
        promptLabelCancel: 'Cancelar'
      });

      return this.processPhoto(photo);
    } catch (error: any) {
      if (error.message?.includes('User cancelled')) {
        logger.info('Usuario canceló la selección');
        return null;
      }
      logger.error('Error en selector de foto', { error });
      throw new Error('No se pudo acceder a la cámara o galería.');
    }
  }

  /**
   * Procesa la foto obtenida y la convierte al formato interno
   */
  private async processPhoto(photo: Photo): Promise<CameraPhoto> {
    const dataUrl = photo.dataUrl || '';
    
    // Crear blob desde dataUrl para subir
    let blob: Blob | undefined;
    if (dataUrl) {
      try {
        const response = await fetch(dataUrl);
        blob = await response.blob();
      } catch (error) {
        logger.warn('No se pudo crear blob desde dataUrl', { error });
      }
    }

    return {
      dataUrl,
      format: photo.format,
      path: photo.path,
      blob
    };
  }

  /**
   * Convierte un dataUrl a File para subir
   */
  dataUrlToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  }
}

export const cameraService = new CameraService();







